<script setup lang="ts">
import { onBeforeMount, ref, Ref } from "vue";
import { useApiClient } from "@/api/client";
import { minLength, required } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import ExportDashboardDO = App.Data.ExportScheduler.ExportDashboardDO;
import DashboardDO = App.Data.DashboardDO;

const { t } = useI18n();

const exportDashboard = defineModel<ExportDashboardDO>();
const availableDashboards: Ref<DashboardDO[]> = ref();
const isLoading = ref(false);

const getAvailableDashboardsForUser = async () => {
	isLoading.value = true;
	try {
		const response = await useApiClient("dashboard").index({
			page: 1,
			itemsPerPage: 10000,
			sortBy: [],
		});
		if (response.isOk()) {
			availableDashboards.value = response.value.data;
		}
	} finally {
		isLoading.value = false;
	}
};

onBeforeMount(() => {
	getAvailableDashboardsForUser();
});
</script>

<template>
	<div class="dashboard-export-config d-flex flex-column ga-2">
		<v-skeleton-loader v-if="isLoading" type="article"></v-skeleton-loader>
		<template v-else>
			<!-- Configuration Section -->
			<v-select
				v-model="exportDashboard.dashboard_id"
				:items="availableDashboards"
				item-title="title"
				item-value="id"
				label="Dashboard ( designed in Print Mode )"
				variant="filled"
				density="comfortable"
				hide-details="auto"
				:rules="[required(t)]"
				placeholder="Select a dashboard to export"
			/>

			<!-- Email Configuration Section -->
			<div class="d-flex align-center ga-1 mt-4">
				<v-icon icon="mdi-email-outline" size="x-small" />
				<span class="font-weight-medium text-subtitle-2">Email Configuration</span>
			</div>

			<v-text-field
				v-model="exportDashboard.email_title"
				label="Subject"
				variant="filled"
				density="comfortable"
				hide-details="auto"
				:rules="[required(t), minLength(t, 10)]"
				placeholder="Enter subject line for the email"
			/>

			<v-textarea
				label="Body"
				hide-details="auto"
				variant="filled"
				v-model="exportDashboard.email_text"
				:rules="[required(t)]"
			/>
		</template>
	</div>
</template>

<style scoped lang="scss">
.dashboard-export-config {
	max-width: 100%;
}
</style>
